// client/src/pages/SubscriptionPage.jsx - Page d'abonnement (Embedded Stripe Payment Element)
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import api from '../api/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { trackEvent } from '../utils/analytics';
import Seo from '../components/Seo';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CheckoutForm = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);

  // Configure Apple Pay / Payment Request Button
  useEffect(() => {
    if (!stripe) return; // Wait for Stripe to be ready

    const pr = stripe.paymentRequest({
      country: 'FR',
      currency: 'eur',
      total: { label: 'Abonnement Premium', amount: 999 }, // 9,99€
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);

        // Handle Apple Pay / Payment Request confirmation
        pr.on('paymentmethod', async (ev) => {
          try {
            setError(null);
            trackEvent('apple_pay_attempt');
            // Crée l'abonnement côté serveur avec ce payment_method
            const { data } = await api.post('/api/stripe/create-subscription', {
              paymentMethodId: ev.paymentMethod.id,
            });

            // Confirmer le paiement avec le payment_method obtenu
            if (data?.client_secret) {
              const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret, {
                payment_method: ev.paymentMethod.id,
              });
              if (confirmError) {
                ev.complete('fail');
                trackEvent('apple_pay_error', { message: confirmError.message });
                setError(confirmError.message || 'Le paiement a échoué.');
                return;
              }
            }

            ev.complete('success');
            trackEvent('apple_pay_success');
            await onSuccess?.();
          } catch (err) {
            ev.complete('fail');
            trackEvent('apple_pay_error', { message: err.message });
            setError(err.response?.data?.message || err.message || 'Une erreur est survenue.');
          }
        });
      }
    });
  }, [stripe, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    try {
      setSubmitting(true);
      setError(null);
      trackEvent('payment_confirm_attempt', { method: 'payment_element' });
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: process.env.STRIPE_SUCCESS_URL || `${window.location.origin}/subscription?success=true`,
        },
      });

      if (confirmError) {
        trackEvent('payment_confirm_error', { method: 'payment_element', message: confirmError.message });
        setError(confirmError.message || 'Le paiement a échoué.');
        return;
      }

      await onSuccess?.();
    } catch (err) {
      trackEvent('payment_confirm_error', { method: 'payment_element', message: err.message });
      setError(err.message || 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {paymentRequest && (
        <div className="mb-4">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: { paymentRequestButton: { theme: 'dark', height: '44px', type: 'long' } },
            }}
          />
          <div className="my-3 text-center text-xs text-gray-400">ou</div>
        </div>
      )}

      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <div className="bg-red-100 text-red-800 p-3 rounded border border-red-200">{error}</div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-3 bg-[#E67E22] text-white rounded-full font-medium hover:bg-[#E67E22]/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all shadow-md"
      >
        {submitting ? 'Traitement…' : 'Payer et activer le Premium'}
      </button>
    </form>
  );
};

const SubscriptionPage = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);

  const seo = (
    <Seo
      title="Abonnement"
      description="Gérez votre abonnement YAQAM Premium, accédez au portail de facturation et profitez de fonctionnalités avancées."
      breadcrumb
    />
  );

  // Rafraîchir le profil après retour de Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get('success');
    if (ok) {
      refreshUser().then(() => setSuccess(true)).catch(() => {});
    }
  }, [refreshUser]);

  // Charger l'état de renouvellement (cancel_at_period_end) lorsque l'utilisateur est premium
  useEffect(() => {
    const loadSubStatus = async () => {
      try {
        if (user?.subscription?.type && ['premium'].includes(user.subscription.type)) {
          const { data } = await api.post('/api/stripe/sync-subscription');
          if (data && typeof data.cancelAtPeriodEnd === 'boolean') {
            setCancelAtPeriodEnd(!!data.cancelAtPeriodEnd);
          }
        } else {
          setCancelAtPeriodEnd(false);
        }
      } catch (_) {}
    };
    loadSubStatus();
  }, [user?.subscription?.type]);
  
  // Démarrer l'abonnement via Payment Element (formulaire intégré)
  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError(null);
      trackEvent('subscribe_click', { plan: 'premium' });
      const { data } = await api.post('/api/stripe/create-subscription');
      if (data?.client_secret) {
        setClientSecret(data.client_secret);
        setShowPayment(true);
        trackEvent('payment_modal_open', { plan: 'premium' });
      } else {
        throw new Error("client_secret non reçu");
      }
    } catch (error) {
      console.error('Erreur Stripe:', error);
      trackEvent('subscribe_init_error', { message: error.message });
      setError(error.response?.data?.message || error.message || 'Erreur lors de l\'initialisation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      trackEvent('billing_portal_click');
      const { data } = await api.post('/api/stripe/portal-session');
      if (data?.url) {
        trackEvent('billing_portal_redirect');
        window.location.href = data.url;
      } else {
        throw new Error("URL du portail non reçue");
      }
    } catch (error) {
      console.error('Erreur portail Stripe:', error);
      trackEvent('billing_portal_error', { message: error.message });
      setError(error.response?.data?.message || error.message || 'Erreur lors de l\'ouverture du portail');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const ok = window.confirm("Confirmer l'arrêt de l'abonnement à la fin de la période en cours ?");
    if (!ok) return;
    try {
      setLoading(true);
      setError(null);
      await api.post('/api/stripe/cancel-subscription');
      await api.post('/api/stripe/sync-subscription').then(res => {
        if (res?.data && typeof res.data.cancelAtPeriodEnd === 'boolean') setCancelAtPeriodEnd(!!res.data.cancelAtPeriodEnd);
      }).catch(() => { setCancelAtPeriodEnd(true); });
      await refreshUser();
      setSuccess(true);
    } catch (error) {
      console.error('Annulation abonnement:', error);
      setError(error.response?.data?.message || error.message || "Impossible d'annuler l'abonnement");
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      await api.post('/api/stripe/resume-subscription');
      await api.post('/api/stripe/sync-subscription').then(res => {
        if (res?.data && typeof res.data.cancelAtPeriodEnd === 'boolean') setCancelAtPeriodEnd(!!res.data.cancelAtPeriodEnd);
      }).catch(() => { setCancelAtPeriodEnd(false); });
      await refreshUser();
      setSuccess(true);
    } catch (error) {
      console.error('Reprise abonnement:', error);
      setError(error.response?.data?.message || error.message || "Impossible de reprendre l'abonnement");
    } finally {
      setLoading(false);
    }
  };
  
  const elementsOptions = useMemo(() => ({
    clientSecret: clientSecret || undefined,
    appearance: { theme: 'stripe' },
  }), [clientSecret]);

  return (
    <>
      {seo}
      <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-[#2C3E50]">Abonnement</h1>
      
      {error && (
        <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-6 border border-red-200">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-[#87A96B]/10 text-[#87A96B] p-4 rounded-lg mb-6 border border-[#87A96B]/30">
          Abonnement Premium activé avec succès! Profitez de toutes les fonctionnalités.
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6 text-[#2C3E50]">Votre abonnement actuel</h2>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-8 border border-gray-100">
            <div className="flex justify-between mb-2">
              <span className="font-medium text-[#2C3E50]">Type:</span>
              <span className="capitalize text-gray-600">{user.subscription.type}</span>
            </div>
            
            {user.subscription.expiresAt && (
              <div className="flex justify-between">
                <span className="font-medium text-[#2C3E50]">Expire le:</span>
                <span className="text-gray-600">
                  {new Date(user.subscription.expiresAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
          
          {user.subscription.type === 'free' ? (
            <div>
              <h3 className="text-lg font-medium mb-4 text-[#2C3E50]">Passer au Premium</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="text-xl font-bold mb-2 text-[#2C3E50]">Free</h4>
                  <p className="text-2xl mb-4 text-[#87A96B]">0€ <span className="text-sm text-gray-600">/ mois</span></p>
                  
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start"><span className="mr-2 text-[#87A96B]">✓</span><span className="text-gray-600">12 suggestions / semaine</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#87A96B]">✓</span><span className="text-gray-600">3 recettes / semaine</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#87A96B]">✓</span><span className="text-gray-600">3 itérations de recettes</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#87A96B]">✓</span><span className="text-gray-600">3 questions à YAQAM</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#87A96B]">✓</span><span className="text-gray-600">Recettes publiques</span></li>
                  </ul>
                  
                  <p className="text-center font-medium text-[#2C3E50]">Votre plan actuel</p>
                </div>
                
                <div className="border-2 border-[#E67E22] rounded-lg p-6 bg-[#E67E22]/5 relative">
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#E67E22] text-white px-4 py-1 rounded-full text-sm font-medium">
                    Recommandé
                  </div>
                  
                  <h4 className="text-xl font-bold mb-2 text-[#2C3E50]">Premium</h4>
                  <p className="text-2xl mb-4 text-[#E67E22]">9,99€ <span className="text-sm text-gray-600">/ mois</span></p>
                  
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start"><span className="mr-2 text-[#E67E22]">✓</span><span className="text-gray-600">120 suggestions / semaine</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#E67E22]">✓</span><span className="text-gray-600">30 recettes / semaine</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#E67E22]">✓</span><span className="text-gray-600">30 itérations de recettes</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#E67E22]">✓</span><span className="text-gray-600">30 questions à l'expert YAQAM</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#E67E22]">✓</span><span className="text-gray-600">Recettes privées</span></li>
                  </ul>
                  
                  {!showPayment ? (
                    <button
                      onClick={handleSubscribe}
                      disabled={loading}
                      className="w-full py-3 bg-[#E67E22] text-white rounded-full font-medium hover:bg-[#E67E22]/90 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all shadow-md"
                    >
                      {loading ? 'Traitement...' : 'S\'abonner maintenant'}
                    </button>
                  ) : null}
                </div>
                <div className="border border-gray-200 rounded-lg p-6 opacity-80">
                  <h4 className="text-xl font-bold mb-2 text-[#2C3E50]">Illimité</h4>
                  <p className="text-2xl mb-4 text-[#2C3E50]">Sur invitation</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start"><span className="mr-2 text-[#2C3E50]">∞</span><span className="text-gray-600">Tout illimité</span></li>
                    <li className="flex items-start"><span className="mr-2 text-[#2C3E50]">✓</span><span className="text-gray-600">Recettes privées</span></li>
                  </ul>
                  <p className="text-center text-sm text-gray-600">Réservé aux invités/amis</p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p className="mb-2">
                  * Paiement sécurisé par carte bancaire. Annulez à tout moment.
                </p>
                <p>
                  * Abonnement mensuel sans engagement, renouvelable automatiquement jusqu'à annulation.
                </p>
              </div>

              {showPayment && clientSecret && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                  onClick={() => {
                    setShowPayment(false);
                    setClientSecret(null);
                  }}
               >
                  <div
                    className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-6 relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      aria-label="Fermer"
                      className="absolute top-3 right-3 rounded-full p-2 text-gray-500 hover:bg-gray-100"
                      onClick={() => {
                        setShowPayment(false);
                        setClientSecret(null);
                      }}
                    >
                      ✕
                    </button>
                    <h4 className="text-xl font-semibold mb-4 text-[#2C3E50]">Finaliser le paiement</h4>
                    <Elements stripe={stripePromise} options={elementsOptions}>
                      <CheckoutForm
                        onSuccess={async () => {
                          try { await api.post('/api/stripe/sync-subscription'); } catch (_) {}
                          await refreshUser();
                          setShowPayment(false);
                          setClientSecret(null);
                          setSuccess(true);
                          trackEvent('subscribe_success', { plan: 'premium' });
                        }}
                      />
                    </Elements>
                    <div className="mt-4 text-center">
                      <button
                        className="text-sm text-gray-500 hover:text-gray-700"
                        onClick={() => {
                          setShowPayment(false);
                          setClientSecret(null);
                        }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-lg font-medium mb-4 text-[#2C3E50]">Vous êtes actuellement abonné au plan {user.subscription.type === 'unlimited' ? 'Illimité' : 'Premium'}</h3>
              
              <div className="bg-[#87A96B]/10 p-6 rounded-lg border border-[#87A96B]/30 mb-6">
                <h4 className="text-xl font-bold mb-4 text-[#2C3E50]">Premium</h4>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#87A96B]">✓</span>
                    <span className="text-gray-600">Planning complet illimité</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#87A96B]">✓</span>
                    <span className="text-gray-600">Liste de courses intelligente</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#87A96B]">✓</span>
                    <span className="text-gray-600">Commande drive automatique</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#87A96B]">✓</span>
                    <span className="text-gray-600">Suggestions anti-gaspi</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#87A96B]">✓</span>
                    <span className="text-gray-600">Personnalisation avancée</span>
                  </li>
                </ul>
                
                <div className="text-center">
                  {user.subscription.expiresAt && (
                    cancelAtPeriodEnd ? (
                      <p className="mb-3 text-[#2C3E50] text-sm">
                        Le renouvellement est désactivé. L’accès reste actif jusqu’au <strong>{new Date(user.subscription.expiresAt).toLocaleDateString()}</strong>.
                      </p>
                    ) : (
                      <p className="mb-3 text-[#2C3E50]">
                        Renouvellement le <strong>{new Date(user.subscription.expiresAt).toLocaleDateString()}</strong>
                      </p>
                    )
                  )}
                  <div className="flex gap-3 justify-center flex-wrap">
                    <button onClick={handleManageBilling} className="px-6 py-3 bg-[#87A96B] text-white rounded-full font-medium hover:bg-[#87A96B]/90 transition-all shadow-md">Gérer la facturation</button>
                    <Link to="/dashboard" className="px-6 py-3 bg-gray-200 text-[#2C3E50] rounded-full font-medium hover:bg-gray-300 transition-all shadow-md">Retour au tableau de bord</Link>
                  </div>
                  <div className="mt-3 text-center">
                    {user.subscription.type === 'premium' && !cancelAtPeriodEnd && (
                      <button
                        onClick={handleCancelSubscription}
                        disabled={loading}
                        className="text-sm text-gray-400 hover:text-gray-600"
                      >
                        Arrêter l'abonnement
                      </button>
                    )}
                    {user.subscription.type === 'premium' && cancelAtPeriodEnd && (
                      <button
                        onClick={handleResumeSubscription}
                        disabled={loading}
                        className="text-sm text-gray-400 hover:text-gray-600"
                      >
                        Reprendre l'abonnement
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SubscriptionPage;
